import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import PublicFooter from "../components/PublicFooter";
import AgencyMark from "../components/AgencyMark";
import FileUpload from "../components/FileUpload";
import { api, formatApiErrorDetail, KABUPATEN_JATENG, JENIS_NARKOBA } from "../lib/api";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Lock,
  Info,
  Loader2,
  AlertTriangle,
  Shield,
  Eye,
  CheckCircle2,
  FileText,
  MapPin,
  Calendar,
  User,
  Send,
  ChevronRight,
} from "lucide-react";

const emptyForm = {
  pelapor_anonim: false,
  nama_pelapor: "",
  nik: "",
  no_telepon: "",
  email_pelapor: "",
  alamat_pelapor: "",
  jenis_narkoba: "",
  kabupaten_kota: "",
  alamat_kejadian: "",
  tanggal_kejadian: "",
  deskripsi_kejadian: "",
  terlapor_info: "",
};

function SectionHeader({ icon: Icon, label, color = "text-[#0B2B5E]", bg = "bg-blue-50" }) {
  return (
    <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
      <div className={`h-8 w-8 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <span className="text-xs tracking-[0.22em] uppercase font-bold text-slate-500">{label}</span>
    </div>
  );
}

export default function ReportPage() {
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.jenis_narkoba || !form.kabupaten_kota || !form.alamat_kejadian.trim() ||
        !form.tanggal_kejadian || !form.deskripsi_kejadian.trim()) {
      toast.error("Mohon lengkapi semua kolom bertanda *");
      return;
    }
    if (!form.pelapor_anonim && !form.nama_pelapor.trim()) {
      toast.error("Nama pelapor wajib diisi atau aktifkan mode anonim");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, file_ids: files.map((f) => f.id) };
      const res = await api.post("/reports", payload);
      toast.success("Laporan berhasil dikirim");
      navigate(`/lapor/sukses/${res.data.nomor_laporan}`);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Gagal mengirim laporan");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="report-page" className="min-h-screen" style={{ background: "#f1f5f9" }}>
      <PublicNavbar />

      {/* ── Hero Banner ────────────────────────────────────── */}
      <section
        className="relative overflow-hidden pt-32 pb-12 lg:pt-40 lg:pb-16"
        style={{ background: "linear-gradient(135deg, #0b2b5e 0%, #0f3d7a 50%, #061830 100%)" }}
      >
        {/* Background textures */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "40px 40px"
        }} aria-hidden />
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)",
          backgroundSize: "28px 28px", opacity: 0.08
        }} aria-hidden />

        {/* Glow orbs */}
        <div className="absolute -top-20 right-0 h-80 w-80 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(250,204,21,0.15) 0%, transparent 70%)" }} aria-hidden />
        <div className="absolute bottom-0 left-1/4 h-64 w-64 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)" }} aria-hidden />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            {/* Breadcrumb steps */}
            <div className="hidden lg:flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-yellow-400 mb-5">
              <span className="text-slate-400">Beranda</span>
              <ChevronRight className="h-3 w-3 text-slate-500" />
              <span>Buat Laporan</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left: title */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                style={{ background: "rgba(250,204,21,0.12)", border: "1px solid rgba(250,204,21,0.3)" }}>
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" style={{ animation: "livePulse 1.8s ease-out infinite" }} />
                <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-yellow-400">Portal Resmi</span>
              </div>
              <h1 className="font-display font-black text-white tracking-tighter"
                style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)", lineHeight: "1.05" }}>
                Laporkan Dugaan<br />
                <span style={{
                  background: "linear-gradient(135deg, #facc15 0%, #f59e0b 40%, #ffffff 70%, #facc15 100%)",
                  backgroundSize: "200% auto",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  animation: "shimmer 4s linear infinite",
                }}>Tindak Narkotika</span>
              </h1>
              <p className="mt-4 text-slate-300 max-w-lg leading-relaxed">
                Seluruh informasi yang Anda kirimkan diterima langsung oleh Ditresnarkoba Polda Jawa Tengah.
                Mohon isi data dengan sejujur-jujurnya.
              </p>
            </div>

            {/* Right: trust stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: Lock, val: "100%", label: "Kerahasiaan", color: "text-emerald-400", glow: "rgba(52,211,153,0.15)" },
                { icon: Shield, val: "Resmi", label: "Ditangani Polri", color: "text-blue-400", glow: "rgba(59,130,246,0.15)" },
                { icon: Eye, val: "24/7", label: "Sistem Aktif", color: "text-yellow-400", glow: "rgba(250,204,21,0.15)" },
              ].map(({ icon: Icon, val, label, color, glow }) => (
                <div key={label} className="rounded-xl p-4 text-center"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", boxShadow: `inset 0 0 16px ${glow}` }}>
                  <Icon className={`h-5 w-5 ${color} mx-auto mb-2`} />
                  <div className={`font-display font-black text-lg ${color}`}>{val}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5 leading-tight">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Form Section ───────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="grid lg:grid-cols-12 gap-8">

          {/* LEFT — sidebar */}
          <aside className="lg:col-span-4 space-y-5">

            {/* Jaminan card */}
            <div className="rounded-2xl overflow-hidden shadow-sm"
              style={{ background: "white", border: "1px solid #e2e8f0" }}>
              <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, #0b2b5e, #3b82f6, #06b6d4)" }} />
              <div className="p-6">
                <div className="flex items-center gap-3 mb-5">
                  <AgencyMark className="h-14 w-14 shrink-0" />
                  <div>
                    <div className="font-display font-bold text-[#0F172A] text-base">Jaminan Kerahasiaan</div>
                    <div className="text-xs text-slate-500 mt-0.5">UU No. 13 Tahun 2006 tentang Perlindungan Saksi & Korban</div>
                  </div>
                </div>
                <ul className="space-y-3">
                  {[
                    { icon: Lock, text: "Identitas pelapor dilindungi penuh oleh petugas.", color: "text-emerald-600", bg: "bg-emerald-50" },
                    { icon: User, text: "Anda dapat memilih melapor secara anonim.", color: "text-blue-600", bg: "bg-blue-50" },
                    { icon: Shield, text: "Data bukti disimpan dengan enkripsi end-to-end.", color: "text-purple-600", bg: "bg-purple-50" },
                  ].map(({ icon: Icon, text, color, bg }) => (
                    <li key={text} className="flex items-start gap-3">
                      <div className={`h-7 w-7 rounded-lg ${bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Icon className={`h-3.5 w-3.5 ${color}`} />
                      </div>
                      <span className="text-sm text-slate-600 leading-relaxed">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Steps card */}
            <div className="rounded-2xl p-6 shadow-sm"
              style={{ background: "white", border: "1px solid #e2e8f0" }}>
              <div className="text-xs tracking-[0.22em] uppercase font-bold text-slate-400 mb-4">Alur Pelaporan</div>
              <ol className="space-y-4">
                {[
                  { num: "01", label: "Isi formulir laporan", active: true },
                  { num: "02", label: "Lampirkan bukti pendukung", active: false },
                  { num: "03", label: "Kirim & simpan nomor laporan", active: false },
                  { num: "04", label: "Pantau status via nomor laporan", active: false },
                ].map((s) => (
                  <li key={s.num} className="flex items-center gap-3">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-black flex-shrink-0 ${s.active ? "bg-[#0B2B5E] text-white" : "bg-slate-100 text-slate-400"}`}>
                      {s.num}
                    </div>
                    <span className={`text-sm ${s.active ? "font-semibold text-[#0B2B5E]" : "text-slate-400"}`}>{s.label}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Warning card */}
            <div className="rounded-2xl p-6 shadow-sm"
              style={{ background: "linear-gradient(135deg, #0b2b5e 0%, #0f3d7a 100%)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-yellow-400 mb-3">
                <div className="h-6 w-6 rounded-lg bg-yellow-400/20 flex items-center justify-center">
                  <AlertTriangle className="h-3.5 w-3.5" />
                </div>
                Penting
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">
                Memberikan laporan palsu dapat dikenai sanksi <strong className="text-white">Pasal 220 KUHP</strong>.
                Pastikan informasi yang Anda sampaikan adalah benar dan dapat dipertanggungjawabkan.
              </p>
            </div>
          </aside>

          {/* RIGHT — form */}
          <form onSubmit={submit} className="lg:col-span-8 space-y-6">

            {/* Anonymous toggle card */}
            <div className="rounded-2xl bg-white shadow-sm overflow-hidden" style={{ border: "1px solid #e2e8f0" }}>
              <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, #facc15, #f59e0b)" }} />
              <div className="p-6 flex items-start justify-between gap-6">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-yellow-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <Label className="font-display font-bold text-[#0F172A] text-base">Lapor sebagai Anonim</Label>
                    <p className="text-sm text-slate-500 mt-1 max-w-md leading-relaxed">
                      Aktifkan untuk menyembunyikan identitas. Kami akan memproses laporan
                      berdasarkan bukti yang Anda sampaikan.
                    </p>
                  </div>
                </div>
                <Switch
                  data-testid="anonim-switch"
                  checked={form.pelapor_anonim}
                  onCheckedChange={(v) => update("pelapor_anonim", v)}
                  className="data-[state=checked]:bg-[#0B2B5E] flex-shrink-0"
                />
              </div>
            </div>

            {/* Main form card */}
            <div className="rounded-2xl bg-white shadow-sm overflow-hidden" style={{ border: "1px solid #e2e8f0" }}>
              <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, #0b2b5e, #3b82f6, #06b6d4)" }} />
              <div className="p-6 sm:p-8 space-y-8">

                {/* Identitas Pelapor */}
                {!form.pelapor_anonim && (
                  <div className="space-y-5">
                    <SectionHeader icon={User} label="Identitas Pelapor" color="text-blue-600" bg="bg-blue-50" />
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700">Nama Lengkap <span className="text-red-500">*</span></Label>
                        <Input
                          value={form.nama_pelapor}
                          onChange={(e) => update("nama_pelapor", e.target.value)}
                          data-testid="input-nama-pelapor"
                          className="h-11 mt-1.5 border-slate-200 focus:border-[#0B2B5E] focus:ring-[#0B2B5E]/20"
                          placeholder="Sesuai KTP"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700">NIK <span className="text-slate-400 font-normal">(opsional)</span></Label>
                        <Input
                          value={form.nik}
                          onChange={(e) => update("nik", e.target.value)}
                          data-testid="input-nik"
                          className="h-11 mt-1.5 font-mono-tight border-slate-200"
                          maxLength={16}
                          placeholder="16 digit"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700">Nomor Telepon</Label>
                        <Input
                          value={form.no_telepon}
                          onChange={(e) => update("no_telepon", e.target.value)}
                          data-testid="input-telepon"
                          className="h-11 mt-1.5 border-slate-200"
                          placeholder="08xx-xxxx-xxxx"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700">Email</Label>
                        <Input
                          type="email"
                          value={form.email_pelapor}
                          onChange={(e) => update("email_pelapor", e.target.value)}
                          data-testid="input-email"
                          className="h-11 mt-1.5 border-slate-200"
                          placeholder="nama@email.com"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-sm font-semibold text-slate-700">Alamat Tinggal</Label>
                        <Input
                          value={form.alamat_pelapor}
                          onChange={(e) => update("alamat_pelapor", e.target.value)}
                          data-testid="input-alamat-pelapor"
                          className="h-11 mt-1.5 border-slate-200"
                          placeholder="Jalan, RT/RW, Kel/Desa, Kecamatan, Kab/Kota"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Detail Kejadian */}
                <div className="space-y-5">
                  <SectionHeader icon={MapPin} label="Detail Kejadian" color="text-red-600" bg="bg-red-50" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold text-slate-700">Jenis Narkoba <span className="text-red-500">*</span></Label>
                      <Select value={form.jenis_narkoba} onValueChange={(v) => update("jenis_narkoba", v)}>
                        <SelectTrigger data-testid="select-jenis-narkoba" className="h-11 mt-1.5 border-slate-200">
                          <SelectValue placeholder="Pilih jenis" />
                        </SelectTrigger>
                        <SelectContent>
                          {JENIS_NARKOBA.map((j) => (
                            <SelectItem key={j} value={j} data-testid={`option-jenis-${j}`}>{j}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700">Kabupaten/Kota <span className="text-red-500">*</span></Label>
                      <Select value={form.kabupaten_kota} onValueChange={(v) => update("kabupaten_kota", v)}>
                        <SelectTrigger data-testid="select-kabupaten" className="h-11 mt-1.5 border-slate-200">
                          <SelectValue placeholder="Pilih wilayah" />
                        </SelectTrigger>
                        <SelectContent>
                          {KABUPATEN_JATENG.map((k) => (
                            <SelectItem key={k} value={k} data-testid={`option-kab-${k}`}>{k}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-sm font-semibold text-slate-700">Alamat Kejadian <span className="text-red-500">*</span></Label>
                      <Input
                        value={form.alamat_kejadian}
                        onChange={(e) => update("alamat_kejadian", e.target.value)}
                        data-testid="input-alamat-kejadian"
                        className="h-11 mt-1.5 border-slate-200"
                        placeholder="Jalan, patokan, nama tempat"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700">Tanggal Kejadian <span className="text-red-500">*</span></Label>
                      <Input
                        type="date"
                        value={form.tanggal_kejadian}
                        onChange={(e) => update("tanggal_kejadian", e.target.value)}
                        data-testid="input-tanggal"
                        className="h-11 mt-1.5 border-slate-200"
                        max={new Date().toISOString().split("T")[0]}
                      />
                    </div>
                  </div>
                </div>

                {/* Kronologi */}
                <div className="space-y-5">
                  <SectionHeader icon={FileText} label="Kronologi & Terlapor" color="text-purple-600" bg="bg-purple-50" />
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-semibold text-slate-700">Kronologi Kejadian <span className="text-red-500">*</span></Label>
                      <Textarea
                        value={form.deskripsi_kejadian}
                        onChange={(e) => update("deskripsi_kejadian", e.target.value)}
                        data-testid="textarea-deskripsi"
                        rows={5}
                        className="mt-1.5 border-slate-200 resize-none"
                        placeholder="Jelaskan kronologi lengkap kejadian yang Anda saksikan atau ketahui..."
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700">
                        Informasi Terlapor
                        <span className="ml-2 text-[11px] font-normal text-slate-400 normal-case tracking-normal">(opsional)</span>
                      </Label>
                      <Textarea
                        value={form.terlapor_info}
                        onChange={(e) => update("terlapor_info", e.target.value)}
                        data-testid="textarea-terlapor"
                        rows={3}
                        className="mt-1.5 border-slate-200 resize-none"
                        placeholder="Ciri-ciri, nama panggilan, kendaraan, atau info terkait terlapor"
                      />
                    </div>
                  </div>
                </div>

                {/* Bukti */}
                <div className="space-y-4">
                  <SectionHeader icon={Shield} label="Bukti Pendukung" color="text-emerald-600" bg="bg-emerald-50" />
                  <FileUpload files={files} onChange={setFiles} testId="report-file-upload" />
                  <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-50 rounded-lg px-3 py-2">
                    <Info className="h-3.5 w-3.5 flex-shrink-0" />
                    Lampirkan foto lokasi, KTP, tangkapan layar, atau dokumen pendukung. Maks. 10MB per file.
                  </div>
                </div>

                {/* Submit bar */}
                <div className="border-t border-slate-100 pt-6">
                  <div className="flex flex-col sm:flex-row items-center gap-4 justify-between">
                    <div className="flex items-start gap-2 text-xs text-slate-400 sm:max-w-sm">
                      <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-500 flex-shrink-0" />
                      Dengan mengirim laporan ini Anda menyatakan informasi yang diberikan benar dan dapat dipertanggungjawabkan.
                    </div>
                    <button
                      type="submit"
                      data-testid="submit-report-btn"
                      disabled={submitting}
                      className="btn-shimmer inline-flex items-center gap-2.5 rounded-xl px-8 py-3.5 font-bold text-sm text-white shadow-lg hover:scale-105 transition-all disabled:opacity-60 disabled:scale-100 flex-shrink-0"
                      style={{ background: submitting ? "#64748b" : "linear-gradient(135deg, #0b2b5e 0%, #0f3d7a 100%)" }}
                    >
                      {submitting ? (
                        <><Loader2 className="h-4 w-4 animate-spin" />Mengirim...</>
                      ) : (
                        <><Send className="h-4 w-4" />Kirim Laporan</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
